import { buildSelectOptions } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { USGSMeasureNormalizer } from 'features/Integrations/features/ConnectedDevices/features/DeviceGraph/utils/measurement.utils';
import { getAvailableFloodThresholds } from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/features/utils/USGSMarkerUtils';

export const buildDevicesBySystemAndTypeMap = (connectedDevices) => {
  const systemMap = new Map();

  connectedDevices?.forEach((device) => {
    const system = device.system_type;
    const deviceType = device.device_type;

    if (!systemMap.has(system)) {
      systemMap.set(system, new Map());
    }

    const deviceTypeMap = systemMap.get(system);

    if (!deviceTypeMap.has(deviceType)) {
      deviceTypeMap.set(deviceType, []);
    }

    deviceTypeMap.get(deviceType).push(device);
  });

  return systemMap;
};

/**
 * Build a value map for a specific device suitable for the Global Actions UI.
 *
 * For most devices this returns the metadata from `dataMaps` (provided by the
 * caller). For USGS Monitoring Station devices the function enumerates the
 * device's time-series datapoints (by `time_series_id` / `parameter_name`) and
 * returns entries with `name` and `type` so the UI can render dropdowns and
 * operators.
 *
 * @param {Object|null} device - The device object (from `connectedDevices`).
 * @param {Object} [dataMaps={}] - Optional metadata map used for non-USGS devices.
 * @returns {Object} A map of keys -> { name: string, type: string } usable by the
 *                   Value/Compare dropdowns. Returns an empty object if no device.
 */
export const buildDeviceValueMap = (device, dataMaps = {}) => {
  if (!device) return {};

  const system = (device.system_type || '').toLowerCase();
  const deviceType = (device.device_type || '').toLowerCase();

  // Base map from configured metadata (if present)
  const baseMap = (dataMaps[system] && dataMaps[system][deviceType]) || {};

  // Special handling for USGS monitoring stations: create entries from time-series data
  if (system === 'usgs' && deviceType === 'monitoring station') {
    //Generate our own basemap

    //Collect time series metadata into a map of time_series_id -> { param, unit_of_measure }
    const timeSeriestoParamNameMap = {};
    for (const timeSeries of device.time_series || []) {
      if (timeSeries.id && timeSeries.parameter_name) {
        // Normalize unit name using USGSMeasureNormalizer
        const normalized = USGSMeasureNormalizer(0, timeSeries.unit_of_measure);
        timeSeriestoParamNameMap[timeSeries.id] = {
          param: timeSeries.parameter_name,
          unit_of_measure:
            normalized && normalized.unit ? normalized.unit : timeSeries.unit_of_measure,
        };
      }
    }

    // Build the display map for this device by iterating through the time series metadata
    const toDisplay = {};
    for (const timeSeriesID of Object.keys(timeSeriestoParamNameMap || {})) {
      const dataAccessorKey = `data.[0].${timeSeriesID}.value`;
      const display = timeSeriestoParamNameMap[timeSeriesID];
      if (display) {
        toDisplay[dataAccessorKey] = {
          name: display.param,
          units: display.unit_of_measure,
          type: 'float',
        };
      }
    }
    // Example output for a USGS Monitoring Station device where the datamap
    // { `data.[0].abc3d43d877d4db788b31466cfed8d6b.value`: { name: 'Gage height', units: 'ft', type: 'float' } }

    const floodThresholdOptions = buildFloodThresholdsOptionsForSelect(device);
    return { ...toDisplay, ...floodThresholdOptions };
  }

  // Default: return base metadata map
  return { ...baseMap };
};

function buildFloodThresholdsOptionsForSelect(device) {
  if (!device || !device.flood_thresholds) {
    return {};
  }
  //check for valid thresholds
  const available = getAvailableFloodThresholds(device);

  // dataAccessorKey `flood_thresholds.${threshold}.stage` -> { name: `${threshold} Flood Stage`, units: 'ft', type: 'float' }
  const options = {};
  for (const threshold of available) {
    // Only add stage thresholds that have a valid stage defined (not -9999)
    if (
      device.flood_thresholds?.[threshold]?.stage !== undefined &&
      device.flood_thresholds?.[threshold]?.stage !== -9999
    ) {
      options[`flood_thresholds.${threshold}.stage`] = {
        name: `${threshold.charAt(0).toUpperCase() + threshold.slice(1)} Flood (Gage Height)`,
        unitName: 'ft',
        units: 'ft',
        type: 'float',
      };
    }

    // Only add flow thresholds that have a valid flow defined (not -9999)
    if (
      device.flood_thresholds?.[threshold]?.flow !== undefined &&
      device.flood_thresholds?.[threshold]?.flow !== -9999
    ) {
      options[`flood_thresholds.${threshold}.flow`] = {
        name: `${threshold.charAt(0).toUpperCase() + threshold.slice(1)} Flood (Discharge)`,
        unitName: 'ft ^3/s',
        units: 'ft3/s',
        type: 'float',
      };
    }
  }
  return options;
}

export const getDeviceTypeString = (device) => {
  return device?.system_type && device?.device_type
    ? `${device.system_type} ${device.device_type}`
    : '';
};
